//THIS SHOULD NOT EXIST, WE SHOULD USE THE SHARED LOGIC INSTEAD - but it's here for now

import { supabase } from '@/integrations/supabase/client';
import { extractIdeasStrict, extractPostStrict } from '@/utils/chatContentParsers';

export interface WebsiteAnalysisData {
  website_content: any[];
  website_info: any;
  key_content_pages: any[];
  post_ideas: any[];
  language: string;
  offering: string;
  goal: string;
}

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
}

export interface ChatSendOptions {
  input: string;
  messages: ChatMessage[];
  currentWebsite?: { id: string; url: string };
  websiteAnalysisData: WebsiteAnalysisData;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setSending: (sending: boolean) => void;
  setInput: (input: string) => void;
}

export const sendChatMessage = async (options: ChatSendOptions): Promise<void> => {
  const { input, messages, currentWebsite, websiteAnalysisData, setMessages, setSending, setInput } = options;
  
  if (!input.trim()) return;
  const text = input.trim();
  setMessages((m) => [...m, { role: 'user', content: text }]);
  setInput(''); // Clear input after adding message to chat
  setSending(true);

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    
    // Add streaming assistant message placeholder
    const assistantMessageId = Date.now();
    setMessages((m) => [...m, { 
      role: 'assistant', 
      content: '', 
      _id: assistantMessageId, 
      _streaming: true, 
      _loading: true, 
      _mode: 'unknown' 
    }]);

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        website_id: currentWebsite?.id,
        website_url: currentWebsite?.url || localStorage.getItem('onboardingWebsite') || undefined,
        message: text,
        history: messages.map(m => ({ role: m.role, content: m.content })),
        website_analysis_data: websiteAnalysisData,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error('Failed to get response');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    let fullContent = '';
    let magicEffect = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = new TextDecoder().decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'magic_start') {
              magicEffect = data.effect;
              // Just show loading state initially
              setMessages((m) => m.map(msg => 
                msg._id === assistantMessageId 
                  ? { ...msg, _loading: true, _mode: 'unknown' }
                  : msg
              ));
            } else if (data.type === 'content') {
              fullContent += data.content;
              
              // Detect mode after we have enough content
              let detectedMode = 'unknown';
              if (fullContent.startsWith('CHAT_MODE:')) {
                detectedMode = 'chat';
              } else if (fullContent.startsWith('IDEA_MODE:')) {
                detectedMode = 'idea';
              } else if (fullContent.startsWith('WRITE_MODE:')) {
                detectedMode = 'write';
              }
              
              // Update mode and content based on detection
              if (detectedMode === 'chat') {
                // Show streaming content for chat mode
                const chatContent = fullContent.replace(/^CHAT_MODE:\s*/, '');
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, content: chatContent, _typing: true, _loading: false, _mode: 'chat' }
                    : msg
                ));
              } else if (detectedMode === 'idea' || detectedMode === 'write') {
                // Keep loading state for JSON modes
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, _loading: true, _mode: detectedMode }
                    : msg
                ));
              } else {
                // Still detecting mode, keep loading
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, _loading: true, _mode: 'unknown' }
                    : msg
                ));
              }
            } else if (data.type === 'magic_end') {
              // Finalize the message
              setMessages((m) => m.map(msg => 
                msg._id === assistantMessageId 
                  ? { ...msg, _streaming: false, _typing: false, _loading: false }
                  : msg
              ));
              
              // Process the final content after a brief delay
              setTimeout(() => {
                processFinalContent(fullContent, assistantMessageId, setMessages, input);
              }, 200);
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }

  } catch (error) {
    console.error('Error sending message:', error);
    setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, I could not respond just now. Please try again.' }]);
  } finally {
    setSending(false);
  }
};

// Process final content and convert to cards
export const processFinalContent = (content: string, messageId: number, setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void, inputMessage: string) => {
  // Parse mode indicator from AI response
  if (content.startsWith('CHAT_MODE:')) {
    // Chat mode: normal conversation text (remove mode indicator)
    const chatContent = content.replace(/^CHAT_MODE:\s*/, '');
    setMessages((m) => m.map(msg => 
      msg._id === messageId 
        ? { role: 'assistant', content: chatContent }
        : msg
    ));
  } else if (content.startsWith('IDEA_MODE:')) {
    // Idea mode: parse JSON after mode indicator
    const jsonContent = content.replace(/^IDEA_MODE:\s*/, '');
    const ideas = extractIdeasStrict(jsonContent);
    if (ideas.length > 0) {
      // Deduplicate by normalised title
      const seen = new Set<string>();
      const uniqueIdeas = ideas.filter((it) => {
        const key = (it.title || '').trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: JSON.stringify({ _type: 'ideas', items: uniqueIdeas, _magic: 'reveal_cards' }) }
          : msg
      ));
    } else {
      // Fallback to chat if JSON parsing fails
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: jsonContent }
          : msg
      ));
    }
  } else if (content.startsWith('WRITE_MODE:')) {
    // Write mode: parse JSON after mode indicator
    const jsonContent = content.replace(/^WRITE_MODE:\s*/, '');
    const draft = extractPostStrict(jsonContent);
    if (draft) {
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: JSON.stringify({ _type: 'post_draft', post: { title: draft.title, intro: draft.intro, digest: draft.digest }, _magic: 'reveal_cards', originalUserPrompt: inputMessage }) }
          : msg
      ));
    } else {
      // Fallback to chat if JSON parsing fails
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: jsonContent }
          : msg
      ));
    }
  } else {
    // Fallback: Check if response contains JSON ideas without mode indicator
    const ideas = extractIdeasStrict(content);
    if (ideas.length > 0) {
      // Deduplicate by normalised title
      const seen = new Set<string>();
      const uniqueIdeas = ideas.filter((it) => {
        const key = (it.title || '').trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: JSON.stringify({ _type: 'ideas', items: uniqueIdeas, _magic: 'reveal_cards' }) }
          : msg
      ));
    } else {
      // Final fallback: treat as chat mode
      setMessages((m) => m.map(msg => 
        msg._id === messageId 
          ? { role: 'assistant', content: content }
          : msg
      ));
    }
  }
};
